import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { useMount, useDidUpdate } from 'react-nb-hooks'

import { useSystemConfig } from 'shared'
import KeyHandler from 'shared/KeyHandler'
import createAction from 'loankeeper-common/helpers/createAction'
import { TabSlider } from 'loankeeper-common/components'

import TabSliderItem from './TabSliderItem'
import { actions, REDUCER_NAME, EVENT_KEYS } from './constants'

function useTabNavigation({ isFocused, handleTabClick, selectedTabRef, list, setFocus }) {
  const handleArrowKey = React.useCallback(
    (nextTabIndex) => {
      if (!isFocused) {
        return
      }

      if (document.getElementsByClassName('modals').length !== 0) {
        return
      }

      const { activeElement } = document
      if (
        activeElement &&
        ['input', 'select', 'button', 'textarea'].indexOf(activeElement.tagName.toLowerCase()) !== -1
      ) {
        return
      }

      handleTabClick(nextTabIndex)
    },
    [isFocused, handleTabClick],
  )

  return [
    {
      targetKey: EVENT_KEYS.LEFT_ARROW_KEY,
      onKey: () => {
        if (selectedTabRef.current > 0) {
          handleArrowKey(selectedTabRef.current - 1)
        }
      },
      deps: [handleArrowKey],
    },
    {
      targetKey: EVENT_KEYS.RIGHT_ARROW_KEY,
      onKey: () => {
        if (selectedTabRef.current < list.length - 1) {
          handleArrowKey(selectedTabRef.current + 1)
        }
      },
      deps: [handleArrowKey, list],
    },
    {
      targetKey: EVENT_KEYS.TAB_KEY,
      onKey: () => {
        setTimeout(() => {
          const { className } = document.activeElement
          if (className && className.indexOf('lk-slider__item--active') !== -1) {
            setFocus(true)
          } else if (isFocused) {
            setFocus(false)
          }
        }, 0)
      },
      deps: [setFocus],
    },
  ]
}

function getTabQuery(location) {
  return new URLSearchParams(location.search).get('tab')
}

const useSelectTab = (setFocus, list, chooseActiveTab) => {
  const dispatch = useDispatch()

  const ref = React.useRef(0)

  const location = useLocation()

  const onSelect = React.useCallback(
    (props) => {
      const tabQuery = getTabQuery(location)

      const selectedTabIndexStr =
        (props.openInModal && ref.current) ||
        (!props.openInModal && tabQuery) ||
        props.activeTab ||
        props.defaultTabIndex

      if (isNaN(selectedTabIndexStr) || list.length === 0) {
        return
      }

      ref.current = parseInt(selectedTabIndexStr, 10) - 1

      const tab = list[ref.current]
      chooseActiveTab(tab)
      dispatch(createAction(actions.CHOOSE_TAB, { name: tab.name }, { name: props.name }))
      setFocus(true)
    },
    [setFocus, list, location, dispatch, chooseActiveTab],
  )

  return [ref, onSelect]
}

const getList = (state, name) => {
  const entry = state[REDUCER_NAME][name]

  let result = []

  if (entry && entry.list) {
    result = entry.list
  }

  return result
}

const TabSliderWrapper = (props) => {
  const dispatch = useDispatch()

  const { isAccountEnabled } = useSystemConfig(['AccEnabled as isAccountEnabled'])

  const list = useSelector((state) => getList(state, props.name))

  const history = useHistory()
  const location = useLocation()

  const [isFocused, setFocus] = React.useState(true)

  const [selectedTabRef, handleSelectTab] = useSelectTab(setFocus, list, props.chooseActiveTab)

  const handleTabClick = React.useCallback(
    (index) => {
      selectedTabRef.current = index + 1

      if (props.openInModal) {
        handleSelectTab(props)
      } else {
        history.replace(`${location.pathname}?tab=${selectedTabRef.current}`)
      }
    },

    // TODO: needs refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, location, handleSelectTab],
  )

  const navigationHandlers = useTabNavigation({
    isFocused,
    handleTabClick,
    selectedTabRef,
    list,
    setFocus,
  })

  useMount(() => {
    dispatch(createAction(actions.INITIALIZE, { tabs: props.tabs, activeTab: props.activeTab }, { name: props.name }))
    handleSelectTab(props)
  })

  const tabQuery = getTabQuery(location)

  useDidUpdate(() => {
    handleSelectTab(props)
  }, [tabQuery, list.length])

  return (
    <>
      {navigationHandlers.map((handler) => (
        <KeyHandler key={handler.targetKey} {...handler} />
      ))}
      <TabSlider className="details-tab-slider-container">
        {list.map((item, index) => (
          <TabSliderItem
            key={item.name}
            index={index}
            data={item}
            active={item.name === props.activeTab}
            handleSelect={handleTabClick}
            isTabSliderFocused={isFocused}
            isAccountEnabled={isAccountEnabled === '1'}
          />
        ))}
      </TabSlider>
    </>
  )
}

TabSliderWrapper.defaultProps = {
  list: [],
  defaultTabIndex: 1,
}

export default React.memo(TabSliderWrapper)
